import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import { rtkQueryErrorActions } from '@skynet/data-rtk-query-error';
import { hasProperty, isRestorableConflict } from 'src/utils/type-guards';

import { DEX_COLD_WALLETS_API_ENDPOINTS as coldWalletsCfg } from '@skynet/data-dex-cold-wallets';
import { DEX_VAULT_DEPLOYER_API_ENDPOINTS as vaultDeployerCfg } from '@skynet/data-dex-vault-deployer';
import { DEX_ADD_VAULTS_API_ENDPOINTS as addVaultsCfg } from '@skynet/data-dex-add-vaults';
import { REGIONS_API_ENDPOINTS as regionsCfg } from '@skynet/data-regions';
import { ROLES_API_ENDPOINTS as rolesCfg } from '@skynet/data-roles';
import { EXCHANGES_API_ENDPOINTS as exchangesCfg } from '@skynet/data-exchanges';
import { TENANTS_API_ENDPOINTS as tenantsCfg } from '@skynet/data-tenants';
import { ITEMS_API_ENDPOINTS as itemsCfg } from '@skynet/data-items';
import { MARKETING_CAMPAIGNS_API_ENDPOINTS as marketingCampaignsCfg } from '@skynet/data-marketing-campaigns';
import { TELEGRAM_GROUPS_API_ENDPOINTS as telegramGroupsCfg } from '@skynet/data-trading-groups';
import { DEX_TASKS_API_ENDPOINTS as dexTasksCfg } from '@skynet/data-dex-tasks';
import { DEX_TOKEN_MAP_API_ENDPOINTS as tokenMapCfg } from '@skynet/data-dex-token-map';
import { SIGNUP_API_ENDPOINTS as signupCfg } from '@skynet/data-signup';
import { TELEGRAM_USERS_API_ENDPOINTS as telegramUsersCfg } from '@skynet/data-telegram-users';

// RTK Query keys endpoints by their *generated* names (e.g. `apiControllerGetColdWallets`),
// not the clean re-export names, so the suppression lists must use the generated ids.
// Sourcing them from the API config keeps them in sync with future codegen renames.
const SILENCED_404_ENDPOINTS: string[] = [
  coldWalletsCfg.listColdWallets.generated,
  coldWalletsCfg.listProjectSnapshots.generated,
  coldWalletsCfg.listColdWalletSnapshots.generated,
];

const SILENCED_403_ENDPOINTS: string[] = [
  // A reader who may see a region but not its IPs gets a 403 on the IP list;
  // `RegionIpsSection` says so in place, so no generic error toast on top of it.
  regionsCfg.listRegionIps.generated,
  // Every Terms viewer may see the balance alert rules, but only Telegram admins may list
  // the users they notify. The Alerts tab names those users `User #<id>` instead, and its
  // form says why the picker is empty.
  telegramUsersCfg.listTelegramUsers.generated,
];

const SILENCED_400_ENDPOINTS: string[] = [
  vaultDeployerCfg.getDeployer.generated,
  coldWalletsCfg.formatAddress.generated,
  // Token-address validation in the create-vault form: an invalid token address is an
  // expected outcome surfaced inline on the field, so its 400 must not raise a notification.
  addVaultsCfg.formatTokenAddress.generated,
  addVaultsCfg.getTokenDetails.generated,
];

// Admin mutations whose call site already raises its own local toast on
// failure — most via `useErrorHandler`'s `withErrorHandling` (custom title,
// Sentry capture, rethrow) with its notification enabled, `syncExchange` via
// `useExchangeChangelogSync`'s own try/catch + `notify.error`. Without this
// list the global middleware raises a second, independent notification for
// the same failure; both `notification.error(...)` calls land almost
// simultaneously and can race inside antd's shared notification state,
// occasionally throwing during render. Unlike the status-keyed lists above,
// this one is status-independent: the local handler owns the failure at
// every status code, not just 400/404.
//
// Endpoints whose local handler passes `showNotification: false` (e.g.
// `deleteRegion`, `updateTenant`, `publishItem`, `updateBotCommand`) must NOT be listed here —
// for those, the global middleware's toast is the only one the user sees.
//
// A `withErrorHandling` wrapper at the call site is NOT on its own enough to
// qualify: the wrapper only sees a failure if the trigger is `.unwrap()`ed.
// Without `.unwrap()` a failed mutation *resolves* with `{ error }` instead of
// rejecting, so the wrapper's catch never runs and no local toast fires —
// listing such an endpoint would leave its failures with zero feedback.
// Among Admin mutations that's `createRole` and `createExchange`; the same
// pattern exists outside Admin (`withdraw` in WithdrawModal, `addTask` /
// `updateTask` in ModalDexTaskForm). Adding `.unwrap()` at those call sites is
// the real fix; until then the global middleware is their only source of truth.
//
// Scope: this list is mostly Admin mutations, plus the sign-up email confirmation
// pair whose page renders every failure inline. The same duplicate-toast race exists for
// several DEX/Loans mutations that DO unwrap with notifications enabled
// (`withdrawGas`, `convert`, `deployVault`, `refreshVault`, `changeStatus`,
// `deleteTask`, `restartBackend`, `updateColdWallet`, `linkColdWallet`) — the
// `AppErrorBoundary` keeps those from white-screening, but they still show two
// toasts. Deliberately left for a follow-up rather than widened here.
//
// Known fragility: membership is hand-maintained and nothing ties it to the
// call sites, so a NEW mutation adopting the local-toast pattern without being
// added here reintroduces the race with no compile-time signal. Sourcing the
// names from the `*_API_ENDPOINTS` configs (above) means codegen renames can't
// silently break it, and `rtkQueryLogger.test.ts` pins both directions for
// every entry — but neither catches an omission. The structural fix is to drop
// per-call-site toasts and let this middleware be the single source of truth,
// deferred as too large for SKY-4437 (see the PR description).
const LOCALLY_HANDLED_MUTATION_ENDPOINTS: string[] = [
  regionsCfg.createRegion.generated,
  regionsCfg.updateRegion.generated,
  // SKY-4605: every region IP mutation is `.unwrap()`ed inside a
  // `withErrorHandling` in `RegionIpsSection`, which toasts the server's
  // validation message itself.
  regionsCfg.createRegionIp.generated,
  regionsCfg.updateRegionIp.generated,
  regionsCfg.deleteRegionIp.generated,
  rolesCfg.updateRole.generated,
  exchangesCfg.updateExchange.generated,
  exchangesCfg.syncExchange.generated,
  tenantsCfg.createTenant.generated,
  itemsCfg.createItem.generated,
  marketingCampaignsCfg.updateMarketingCampaign.generated,
  marketingCampaignsCfg.deleteMarketingCampaign.generated,
  // SKY-4957: `AddMarketingCampaigns` `.unwrap()`s the create inside a
  // `withErrorHandling`, which toasts "Failed to add marketing campaign" itself.
  // That call site (and the list's row delete above) now lives in
  // `@skynet/sky-marketing`, outside this app: nothing compile-checks the pair,
  // so `pages/Admin/__tests__/Marketing.toasts.test.tsx` renders both against
  // this middleware and pins exactly one toast per failure.
  marketingCampaignsCfg.createMarketingCampaign.generated,
  telegramGroupsCfg.updateTelegramGroup.generated,
  telegramGroupsCfg.changeTelegramGroupBot.generated,
  // SKY-4558: every add/remove-members call site owns its own failure
  // toast via `withErrorHandling`, for every status code — so the global
  // middleware must stay out of the way for all of them, not just the ones
  // that render a specific message. Only `AddTradingGroupMembersModal` maps
  // the documented synchronous 400s (`MEMBERS_UNKNOWN_USER` etc.) to inline
  // text; `TradingGroupMembersTab.handleRemove` and `MembersJobReportModal`'s
  // skipped-rows retry surface the same codes as a generic toast instead.
  telegramGroupsCfg.addTelegramGroupMembers.generated,
  telegramGroupsCfg.removeTelegramGroupMembers.generated,
  // SKY-4559: same rationale as the single-group pair above. The two form
  // call sites (`BulkAddMembersModal`/`BulkRemoveMembersModal`, which also
  // render the scope-driven "offboarding sweep" flow when nothing is
  // selected) map the documented synchronous 400/404s (`MEMBERS_TARGET_REQUIRED`,
  // `MEMBERS_SCOPE_EMPTY`, `MEMBERS_BULK_TOO_LARGE`) to inline text;
  // `BulkMembersJobReportModal`'s skipped-rows retry owns its own failure
  // toast via `withErrorHandling`.
  telegramGroupsCfg.bulkAddTelegramGroupMembers.generated,
  telegramGroupsCfg.bulkRemoveTelegramGroupMembers.generated,
  // SKY-4604: every exchange IP and region-link mutation is `.unwrap()`ed
  // inside a `withErrorHandling` in `ExchangeIpsSection`, which toasts the
  // server's validation message itself.
  exchangesCfg.createExchangeIp.generated,
  exchangesCfg.updateExchangeIp.generated,
  exchangesCfg.deleteExchangeIp.generated,
  exchangesCfg.createExchangeRegion.generated,
  exchangesCfg.deleteExchangeRegion.generated,
  // SKY-3394: `ModalDexTaskForm` `.unwrap()`s both inside a `withErrorHandling`, which toasts
  // "Failed to add/update task" itself.
  dexTasksCfg.createDexTask.generated,
  dexTasksCfg.updateDexTask.generated,
  // SKY-4827: `EmailCodeConfirmation` maps every confirm/resend failure (invalid or
  // expired code, exhausted attempts, rate limits, cooldown, unknown) to an inline
  // notice next to the code input, so a global toast would only duplicate it.
  signupCfg.confirmEmail.generated,
  signupCfg.resendConfirmationEmail.generated,
  // SKY-4830: the password reset page maps every failure of its steps (invalid
  // code, lockout, rate limits, cooldown, expired session, unknown) to an inline
  // notice or a step change. `requestPasswordReset` is also called by the
  // profile's `ChangePassword`, which toasts its failures via `withErrorHandling`.
  signupCfg.requestPasswordReset.generated,
  signupCfg.verifyPasswordResetEmail.generated,
  signupCfg.verifyPasswordReset2fa.generated,
  signupCfg.verifyPasswordResetRecoveryCode.generated,
  signupCfg.resetPassword.generated,
  // SKY-4956: `AddMappingRule` and `TokenMapDropDownActions` (`@skynet/sky-token-map`)
  // `.unwrap()` the bind/unbind inside a `withErrorHandling`, which toasts
  // "Failed to add mapping rule" / "Failed to unbind mapping" with the server's
  // message itself. The guard that the call sites really unwrap lives in that
  // package, not here: their "shows its own error notification when the server
  // rejects …" tests.
  // Changing either call site's error handling must keep those tests passing.
  tokenMapCfg.bindTokenMap.generated,
  tokenMapCfg.unbindTokenMap.generated,
];

// Queries whose page renders every failure itself. The password reset session
// answers 401 to every visitor without one — the normal start of the flow — and
// the page shows a retry for any other failure (SKY-4830).
const LOCALLY_HANDLED_QUERY_ENDPOINTS: string[] = [signupCfg.getPasswordResetSession.generated];

interface RtkQueryAction {
  payload: {
    status: number;
  };
  meta: {
    arg: {
      type: 'query' | 'mutation';
      endpointName: string;
    };
  };
}

// Type guard function to safely check if action is RtkQueryAction
const isRtkQueryAction = (action: unknown): action is RtkQueryAction => {
  // Check if action has payload with status
  if (!hasProperty(action, 'payload') || !hasProperty(action.payload, 'status')) {
    return false;
  }

  if (typeof action.payload.status !== 'number') {
    return false;
  }

  // Check if action has meta.arg with type and endpointName
  if (!hasProperty(action, 'meta') || !hasProperty(action.meta, 'arg')) {
    return false;
  }

  const { arg } = action.meta;
  if (!hasProperty(arg, 'type') || !hasProperty(arg, 'endpointName')) {
    return false;
  }

  return (
    typeof arg.type === 'string' &&
    ['query', 'mutation'].includes(arg.type) &&
    typeof arg.endpointName === 'string'
  );
};

export const rtkQueryErrorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action: unknown) => {
    if (isRejectedWithValue(action) && isRtkQueryAction(action)) {
      if (
        (action.payload.status === 404 &&
          SILENCED_404_ENDPOINTS.includes(action.meta.arg.endpointName)) ||
        (action.payload.status === 400 &&
          SILENCED_400_ENDPOINTS.includes(action.meta.arg.endpointName)) ||
        (action.payload.status === 403 &&
          SILENCED_403_ENDPOINTS.includes(action.meta.arg.endpointName))
      ) {
        // Because the lack of wallets comes in the form of an error 404, we don't want to show an error notification
        return next(action);
      }

      if (
        (action.meta.arg.type === 'mutation' &&
          LOCALLY_HANDLED_MUTATION_ENDPOINTS.includes(action.meta.arg.endpointName)) ||
        (action.meta.arg.type === 'query' &&
          LOCALLY_HANDLED_QUERY_ENDPOINTS.includes(action.meta.arg.endpointName))
      ) {
        // The call site already raises its own toast via `withErrorHandling`;
        // skip the global one so the user doesn't see both.
        return next(action);
      }

      // A conflict carrying a `restoreId` is not a plain failure: the record already
      // exists but is soft-deleted, and the calling feature turns that into a
      // "Restore instead?" prompt. Skip the generic notification so it isn't shown
      // alongside that prompt. Deliberately endpoint-agnostic — `restoreId` is a
      // cross-resource contract, so every soft-deletable entity gets this for free.
      if (isRestorableConflict(action.payload)) {
        return next(action);
      }
      api.dispatch(rtkQueryErrorActions.setError(action.payload));
      return next(action);
    }

    return next(action);
  };
